import React, { useState } from 'react'
import { withRouter } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Row, Col, Label, Input, Button,
} from 'reactstrap'
import { submitLicenseRequest } from '../../actions/cspLicensesActions'
import BasketLineRow from './BasketLineRow'

const CSPLicenseCheckoutModal = ({ isOpen, toggle, tenantId, companyName, threshold, history }) => {
  const dispatch = useDispatch()
  const cart = useSelector((s) => s.cspLicenses.cart)
  const submitting = useSelector((s) => s.cspLicenses.submitting)

  const [cwTicketId, setCwTicketId] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  // onClosed rather than an effect on isOpen: it fires once the modal has actually
  // closed, so there is no extra render of a closing modal, and it still runs when
  // the user dismisses with Escape or the backdrop.
  const resetForm = () => {
    setCwTicketId('')
    setAcknowledged(false)
  }

  const handleSubmit = async () => {
    const ticketNum = parseInt(cwTicketId, 10)
    if (!Number.isFinite(ticketNum) || ticketNum <= 0) return
    if (!acknowledged) return
    if (cart.lines.length === 0) return

    const result = await dispatch(submitLicenseRequest({
      dickerDataTenantId: tenantId,
      cwTicketId: ticketNum,
      approvalAcknowledged: acknowledged,
      lines: cart.lines.map((l) => ({
        cspSubscriptionId: l.cspSubscriptionId,
        stockCode: l.stockCode,
        skuDisplayName: l.skuDisplayName,
        stockDescription: l.stockDescription,
        previousQuantity: l.previousQuantity,
        requestedQuantity: l.requestedQuantity,
      })),
    }))

    if (result?.ok && result.payload?.requestId) {
      toggle()
      // Send the user to My Requests so they can watch the attempt
      // history populate; include the new request id in the URL hash so
      // the table can highlight the row on landing.
      history.push(`/CSPLicenses/MyRequests#${result.payload.requestId}`)
    }
  }

  const ticketParsed = parseInt(cwTicketId, 10)
  const ticketValid = Number.isFinite(ticketParsed) && ticketParsed > 0
  const canSubmit = ticketValid && acknowledged && cart.lines.length > 0 && !submitting
  // Over-threshold baskets are queued for approval. Surface that here
  // so the user knows clicking Submit doesn't mean "applied immediately".
  // Absolute delta to match the server-side gate: a -8 removal needs
  // peer review for the same reason a +8 addition does (large
  // licence cost swing, equally easy to fat-finger).
  const willQueueForApproval = typeof threshold === 'number'
    && cart.lines.some((l) => Math.abs(l.requestedQuantity - l.previousQuantity) > threshold)

  return (
    <Modal isOpen={isOpen} toggle={toggle} onClosed={resetForm} size="lg">
      <ModalHeader toggle={toggle}>
        Checkout — {companyName || 'Tenant'}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <Label className="font-weight-bold">ConnectWise ticket #</Label>
            <Input
              type="number"
              min={1}
              placeholder="e.g. 123456"
              value={cwTicketId}
              onChange={(e) => setCwTicketId(e.target.value)}
              bsSize="sm"
              disabled={submitting}
            />
            <small className="text-muted">
              We'll check the ticket is open and belongs to this customer
              before submitting.
            </small>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Label className="font-weight-bold mb-1">Basket</Label>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th className="text-right">Before</th>
                  <th className="text-right">After</th>
                </tr>
              </thead>
              <tbody>
                {cart.lines.map((l) => (
                  <BasketLineRow key={l.stockCode}
                    skuDisplayName={l.skuDisplayName}
                    stockCode={l.stockCode}
                    stockDescription={l.stockDescription}
                    previousQuantity={l.previousQuantity}
                    requestedQuantity={l.requestedQuantity}
                  />
                ))}
              </tbody>
            </table>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <div className="d-flex align-items-start">
              <Input
                type="checkbox"
                className="m-0 mr-2"
                style={{ position: 'relative', marginTop: 4, transform: 'scale(1.2)' }}
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={submitting}
                id="approval-ack"
              />
              <Label for="approval-ack" className="small mb-0">
                I have received approval from the client OR my Partner to make
                these licence changes.
              </Label>
            </div>
          </Col>
        </Row>

        {willQueueForApproval && (
          <div className="alert alert-info small py-2 mt-3 mb-0">
            This change is larger than your auto-approval limit. After
            you submit, it'll wait in My Requests until another team
            member can review it.
          </div>
        )}

      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={submitting}>Cancel</Button>
        <Button color="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm mr-1" role="status" />
              Submitting…
            </>
          ) : (willQueueForApproval ? 'Submit for approval' : 'Submit to Dicker Data')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default withRouter(CSPLicenseCheckoutModal)
